import React from "react";

import { DataGrid, GridToolbarQuickFilter } from "@mui/x-data-grid";
import Box from "@mui/material/Box";

// Maps a raw suppression-category value from the API to a friendly label.
const formatSuppressionStatus = (value) => {
    if (value === undefined || value === null || value === "") {
        return "";
    }
    const normalized = String(value).toLowerCase().replace(/[\s_]+/g, "-");
    if (normalized.includes("short")) return "Grace";
    if (normalized.includes("repeat")) return "Concern";
    if (normalized.includes("long")) return "Flagged";
    return String(value);
};

// Formats a TTL in seconds into a human-readable duration.
const formatTtl = (ttl) => {
    if (ttl === undefined || ttl === null || ttl === "") {
        return "";
    }
    const totalSeconds = Number(ttl);
    if (isNaN(totalSeconds)) return String(ttl);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    if (hours > 0) return `${hours}h ${minutes}m`;
    if (minutes > 0) return `${minutes}m`;
    return `${totalSeconds}s`;
};

function QuickSearchToolbar() {
    return (
        <Box
            sx={{
                p: 0.5,
                pb: 0,
            }}
        >
            <GridToolbarQuickFilter />
        </Box>
    );
}

export default function Basic(props) {
    let rows = [];

    if (props.suppressedDevices !== undefined && props.suppressedDevices !== null) {
        const data = props.suppressedDevices;

        // Determine whether this is the categorized structure returned by
        // listSuppressedDevices, e.g. { longterm: [...], repeat: [...], short: [...] }.
        const isCategorized =
            !Array.isArray(data) &&
            typeof data === "object" &&
            Object.values(data).some((value) => Array.isArray(value));

        if (isCategorized) {
            // Determine which category a MAC belongs to using a strict priority
            // order. A device is only ever displayed once:
            //   1. long term  (count >= 1000) -> Flagged
            //   2. repeat      (count >= 100)  -> Concern
            //   3. short term  (count >= 10)   -> Grace
            // The highest-priority matching rule wins.
            const categoryRules = [
                { match: "long", threshold: 1000, priority: 1 },
                { match: "repeat", threshold: 100, priority: 2 },
                { match: "short", threshold: 10, priority: 3 },
            ];

            const deviceByMac = {};

            Object.keys(data).forEach((category) => {
                const devices = data[category];
                if (!Array.isArray(devices)) return;

                const normalizedCategory = String(category)
                    .toLowerCase()
                    .replace(/[\s_]+/g, "");
                const rule = categoryRules.find((r) =>
                    normalizedCategory.includes(r.match)
                );
                if (!rule) return;

                devices.forEach((device) => {
                    const macAddress =
                        device.mac ||
                        device.MACAddress ||
                        device.MacAddress ||
                        device.MAC ||
                        "";
                    const count = Number(device.count);

                    // Only include devices that meet this category's threshold.
                    if (isNaN(count) || count < rule.threshold) return;

                    const existing = deviceByMac[macAddress];
                    if (
                        existing === undefined ||
                        rule.priority < existing.priority
                    ) {
                        deviceByMac[macAddress] = {
                            priority: rule.priority,
                            category,
                        mac: macAddress,
                            count: device.count,
                            ttl: device.ttl,
                        };
                    }
                });
            });

            rows = Object.values(deviceByMac).map((device, index) => ({
                id: `${device.category}-${device.mac || index}`,
                mac: device.mac,
                status: "suppressed",
                suppressionStatus: formatSuppressionStatus(device.category),
                count: device.count !== undefined ? device.count : "",
                ttl: formatTtl(device.ttl),
            }));
        } else {
            // Support both an array of devices and an object keyed by MAC/id.
            const entries = Array.isArray(data)
                ? data.map((item, index) => [index, item])
                : Object.keys(data).map((key) => [key, data[key]]);

            for (const [key, device] of entries) {
                // The device value may be a string (just the MAC) or an object.
                const isObject = device !== null && typeof device === "object";

                const macAddress = isObject
                    ? device.mac ||
                      device.MACAddress ||
                      device.MacAddress ||
                      device.MAC ||
                      key
                    : device || key;

                const suppressionStatusRaw = isObject
                    ? device.SuppressionStatus ||
                      device.SuppressionTerm ||
                      device.suppressionStatus ||
                      device.Term ||
                      device.term
                    : undefined;

                rows.push({
                    id: key,
                    mac: macAddress,
                    status: "suppressed",
                    suppressionStatus: formatSuppressionStatus(suppressionStatusRaw),
                    count: isObject && device.count !== undefined ? device.count : "",
                    ttl: isObject ? formatTtl(device.ttl) : "",
                });
            }
        }
    }

    const columns = [
        {
            headerName: "MAC Address",
            field: "mac",
            flex: 0.3,
            minWidth: 180,
        },
        // {
        //     headerName: "Status",
        //     field: "status",
        //     flex: 0.2,
        //     minWidth: 130,
        // },
        {
            headerName: "Status",
            field: "suppressionStatus",
            flex: 0.25,
            minWidth: 160,
        },
        {
            headerName: "Attempts",
            field: "count",
            flex: 0.15,
            minWidth: 110,
        },
        {
            headerName: "Time Remaining",
            field: "ttl",
            flex: 0.2,
            minWidth: 140,
        },
    ];

    return (
        <div style={{ height: 400, width: "100%" }}>
            <DataGrid
                rows={rows}
                columns={columns}
                hideFooterPagination={false}
                hideFooter={false}
                slots={{ toolbar: QuickSearchToolbar }}
                checkboxSelection={true}
                disableRowSelectionOnClick={true}
                onRowSelectionModelChange={(selectionModel) => {
                    if (props.onSelectionChange) {
                        const selectedRows = rows.filter((row) =>
                            selectionModel.includes(row.id)
                        );
                        props.onSelectionChange(selectionModel, selectedRows);
                    }
                }}
                localeText={{
                    noRowsLabel: "No Suppressed Devices Found",
                }}
                initialState={{
                    sorting: {
                        sortModel: [{ field: "mac", sort: "asc" }],
                    },
                }}
            />
        </div>
    );
}
